import React, { useRef, useState } from "react";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import CircularProgress from "@mui/material/CircularProgress";
import "../partsCatalogPartSearch/partSearchTab.css";
import "./bulkUploadTab.css";
import { uploadPartsCatalogBulkFile } from "../../graphql/partsCatalogQueries";
import {
  messageFromApiError,
  messageFromApiResponse,
} from "../../utils/apiMessages";

const UPLOAD_TYPE_OPTIONS = [
  { value: "bulk-upload/description", label: "parts - description" },
  { value: "bulk-upload/title", label: "parts - title" },
  { value: "bulk-upload/part-source", label: "parts - source" },
  { value: "bulk-upload/manufacturer-code", label: "manufacturer-code" },
];

const DESCRIPTION_UPLOAD_TYPE = "bulk-upload/description";
const DESCRIPTION_FILENAME_PREFIX = "PartFeatureCopy_";
const TITLE_UPLOAD_TYPE = "bulk-upload/title";
const TITLE_FILENAME_PREFIX = "PartTitle_";
const SOURCE_UPLOAD_TYPE = "bulk-upload/part-source";
const SOURCE_FILENAME_PREFIX = "PartSource_";
const MANUFACTURER_CODE_UPLOAD_TYPE = "bulk-upload/manufacturer-code";
const MANUFACTURER_CODE_FILENAME_PREFIX = "PartMfgCd_";

function getFilenameError(uploadType, fileName) {
  const name = fileName != null ? String(fileName) : "";
  if (uploadType === TITLE_UPLOAD_TYPE) {
    if (!name.startsWith(TITLE_FILENAME_PREFIX)) {
      return `For parts - title upload, the filename must start with ${TITLE_FILENAME_PREFIX}`;
    }
  } else if (uploadType === DESCRIPTION_UPLOAD_TYPE) {
    if (!name.startsWith(DESCRIPTION_FILENAME_PREFIX)) {
      return `For parts - description upload, the filename must start with ${DESCRIPTION_FILENAME_PREFIX}`;
    }
  } else if (uploadType === SOURCE_UPLOAD_TYPE) {
    if (!name.startsWith(SOURCE_FILENAME_PREFIX)) {
      return `For parts - source upload, the filename must start with ${SOURCE_FILENAME_PREFIX}`;
    }
  } else if (uploadType === MANUFACTURER_CODE_UPLOAD_TYPE) {
    if (!name.startsWith(MANUFACTURER_CODE_FILENAME_PREFIX)) {
      return `For manufacturer-code upload, the filename must start with ${MANUFACTURER_CODE_FILENAME_PREFIX}`;
    }
  }
  return null;
}

export default function BulkUploadTab() {
  const [sectionOpen, setSectionOpen] = useState(true);
  const [uploadType, setUploadType] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusTone, setStatusTone] = useState("info");
  const fileInputRef = useRef(null);

  const canUpload = Boolean(uploadType && selectedFile) && !uploading;

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const validateSelection = (type, file) => {
    if (type && file) {
      const err = getFilenameError(type, file.name);
      if (err) {
        setStatusTone("error");
        setStatusMessage(err);
        return false;
      }
    }
    setStatusMessage(null);
    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    if (file) {
      validateSelection(uploadType, file);
    } else {
      setStatusMessage(null);
    }
  };

  const handleUploadTypeChange = (e) => {
    const nextType = e.target.value;
    setUploadType(nextType);
    if (selectedFile) {
      validateSelection(nextType, selectedFile);
    } else {
      setStatusMessage(null);
    }
  };

  const handleUpload = async () => {
    if (!uploadType || !selectedFile) return;

    const err = getFilenameError(uploadType, selectedFile.name);
    if (err) {
      setStatusTone("error");
      setStatusMessage(err);
      return;
    }

    setUploading(true);
    setStatusMessage(null);
    try {
      const result = await uploadPartsCatalogBulkFile({
        file: selectedFile,
        uploadType,
      });
      const okMsg = messageFromApiResponse(
        result,
        `Uploaded "${selectedFile.name}" successfully.`
      );
      setStatusTone("success");
      setStatusMessage(okMsg);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      setStatusTone("error");
      setStatusMessage(
        messageFromApiError(err, "Upload failed. Please try again.")
      );
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="partSearchTab">
      <section className="pscSection">
        <button
          type="button"
          className="pscSectionHeader"
          onClick={() => setSectionOpen((o) => !o)}
          aria-expanded={sectionOpen}
        >
          <span className="pscSectionTitle">Bulk Upload</span>
          <span className="pscSectionToggle" aria-hidden>
            {sectionOpen ? (
              <RemoveIcon fontSize="small" />
            ) : (
              <AddIcon fontSize="small" />
            )}
          </span>
        </button>
        {sectionOpen && (
          <div className="pscSectionBody">
            <div className="pscCriteriaLayout">
              <div className="pscFieldGrid bucFieldGrid">
                <div className="pscField bucTypeField">
                  <label htmlFor="buc-uploadType">Type</label>
                  <div className="bucFileRow">
                    <select
                      id="buc-uploadType"
                      className="pscInput"
                      value={uploadType}
                      onChange={handleUploadTypeChange}
                      disabled={uploading}
                    >
                      <option value="">-- Select --</option>
                      {UPLOAD_TYPE_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="bucHiddenFileInput"
                      accept=".csv,.xlsx,.xls,.txt"
                      onChange={handleFileChange}
                      tabIndex={-1}
                    />
                    <button
                      type="button"
                      className="pscBtnSecondary"
                      onClick={handleBrowseClick}
                      disabled={uploading}
                    >
                      Browse
                    </button>
                  </div>
                </div>
                {selectedFile && (
                  <p className="bucSelectedFile" title={selectedFile.name}>
                    Selected file: <strong>{selectedFile.name}</strong>
                  </p>
                )}
                {statusMessage && (
                  <p
                    className={
                      statusTone === "error"
                        ? "bucStatusError"
                        : statusTone === "success"
                          ? "bucStatusSuccess"
                          : "bucStatusInfo"
                    }
                    role="status"
                  >
                    {statusMessage}
                  </p>
                )}
              </div>
              <div className="pscActions">
                <button
                  type="button"
                  className="pscBtnPrimary pscBtnPrimaryWithSpinner"
                  onClick={handleUpload}
                  disabled={!canUpload}
                  aria-busy={uploading}
                >
                  {uploading ? (
                    <>
                      <CircularProgress
                        className="pscBtnSpinner"
                        color="inherit"
                        size={20}
                        thickness={4}
                        aria-hidden
                      />
                      <span>Uploading…</span>
                    </>
                  ) : (
                    "Upload"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
